const API_BASE = '/api/vc-usr/chat';

function authHeaders(hasBody) {
  const token = localStorage.getItem('authToken');
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
  };
}

async function chatApi(path, { method = 'GET', body } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: authHeaders(body !== undefined),
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) {
    const err = new Error(json.message || `Erro ${res.status}`);
    err.status = res.status;
    err.error = json.error || null;
    err.holderId = json.holderId ?? json.payload?.holderId ?? null;
    err.payload = json.payload || json.data || null;
    throw err;
  }
  return json;
}

export function fetchChatSession() {
  return chatApi('/session');
}

export function fetchChatBadges() {
  return chatApi('/badges');
}

export function fetchMyChatTeams() {
  return chatApi('/my-teams');
}

/** Todos os times do tenant (seletor de transferência). */
export function fetchTenantChatTeams() {
  return chatApi('/tenant-teams');
}

export function fetchTeamAgents(teamId) {
  return chatApi(`/teams/${teamId}/agents`);
}

export function fetchConversations(query = {}) {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([k, v]) => {
    if (v == null || v === '') return;
    params.set(k, String(v));
  });
  const qs = params.toString();
  return chatApi(`/conversations${qs ? `?${qs}` : ''}`);
}

export function fetchConversation(conversationId) {
  return chatApi(`/conversations/${conversationId}`);
}

export function fetchMessages(conversationId) {
  return chatApi(`/conversations/${conversationId}/messages`);
}

export function sendChatMessage(conversationId, payload) {
  return chatApi(`/conversations/${conversationId}/messages`, {
    method: 'POST',
    body: payload,
  });
}

export function updateChatStatus(conversationId, status) {
  return chatApi(`/conversations/${conversationId}/status`, {
    method: 'POST',
    body: { status },
  });
}

export function fetchConversationSessions(conversationId) {
  return chatApi(`/conversations/${conversationId}/sessions`);
}

export function fetchConversationOs(conversationId) {
  return chatApi(`/conversations/${conversationId}/os`);
}

/** Extrai lista + meta de OS removidas no ERP a partir do GET .../os. */
export function parseConversationOsResponse(osRes) {
  const items = Array.isArray(osRes?.data) ? osRes.data : [];
  const removedOs = Array.isArray(osRes?.removedOs) ? osRes.removedOs : [];
  return {
    items,
    removedOs,
    wasOnlyOs: Boolean(osRes?.wasOnlyOs),
    releasedQueue: Boolean(osRes?.releasedQueue),
  };
}

export function patchConversationOs(conversationId, osId, body) {
  return chatApi(
    `/conversations/${conversationId}/os/${encodeURIComponent(String(osId || ''))}`,
    { method: 'PATCH', body }
  );
}

/** Repassa OS pendente pra outro atendente do time de pré-atendimento. */
export function handoffConversationOs(conversationId, osId, toAgentId, opts = {}) {
  const body = { toAgentId };
  if (opts.claimUnassigned === false) body.claimUnassigned = false;
  if (opts.syncChatwootAssignee === false) body.syncChatwootAssignee = false;
  return chatApi(
    `/conversations/${conversationId}/os/${encodeURIComponent(String(osId || ''))}/handoff`,
    { method: 'PATCH', body }
  );
}

/** Limpa assigned_agent_id das OS pendentes/adiadas do agente atual. */
export function clearConversationOsAssignee(conversationId) {
  return chatApi(`/conversations/${conversationId}/os/clear-assignee`, {
    method: 'POST',
  });
}

/** Reivindica OS sem dono. Body opcional: { osId }. Sem osId = todas elegíveis. */
export function claimUnassignedConversationOs(conversationId, body = {}) {
  return chatApi(`/conversations/${conversationId}/os/claim-unassigned`, {
    method: 'POST',
    body: body || {},
  });
}

/** Disparado após forward/close confirmados — painel recarrega lista de OS. */
export const BCH_CONVERSATION_OS_UPDATED = 'bch:conversation-os-updated';

export function notifyConversationOsUpdated(conversationId, osId, status) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(BCH_CONVERSATION_OS_UPDATED, {
      detail: {
        conversationId: conversationId != null ? String(conversationId) : null,
        osId: osId != null ? String(osId) : null,
        status: status || null,
      },
    })
  );
}

export function fetchPortalCustomer(conversationId) {
  return chatApi(`/conversations/${conversationId}/portal-customer`);
}

export function fetchPortalCustomerAccessSites(conversationId) {
  return chatApi(`/conversations/${conversationId}/portal-customer/access-sites`);
}

export function fetchPortalCustomerProfilePhotoUrl(conversationId) {
  return chatApi(`/conversations/${conversationId}/portal-customer/profile-photo/url`);
}

export function updatePortalCustomer(conversationId, body) {
  return chatApi(`/conversations/${conversationId}/portal-customer`, {
    method: 'PATCH',
    body,
  });
}

export function sendTypingStatus(conversationId, status) {
  return chatApi(`/conversations/${conversationId}/typing`, {
    method: 'POST',
    body: { status },
  });
}

export function markConversationRead(conversationId) {
  return chatApi(`/conversations/${conversationId}/read`, { method: 'POST' });
}

export function markConversationUnread(conversationId) {
  return chatApi(`/conversations/${conversationId}/unread`, { method: 'POST' });
}

export function assignConversationAgent(conversationId) {
  return chatApi(`/conversations/${conversationId}/assign-agent`, { method: 'POST' });
}

/** Desvincular atendente (devolver para a fila) — assigneeId null explícito. */
export function unassignConversationAgent(conversationId) {
  return chatApi(`/conversations/${conversationId}/assign-agent`, {
    method: 'POST',
    body: { assigneeId: null },
  });
}

export function transferConversationTeam(conversationId, teamId) {
  return chatApi(`/conversations/${conversationId}/transfer-team`, {
    method: 'POST',
    body: { teamId },
  });
}

export function fetchCannedResponses() {
  return chatApi('/canned-responses');
}

export function createCannedResponse(body) {
  return chatApi('/canned-responses', {
    method: 'POST',
    body,
  });
}

export function updateCannedResponse(id, body) {
  return chatApi(`/canned-responses/${id}`, {
    method: 'PATCH',
    body,
  });
}

export function deleteCannedResponse(id) {
  return chatApi(`/canned-responses/${id}`, { method: 'DELETE' });
}

export function presignChatAttachment(conversationId, body) {
  return chatApi(`/conversations/${conversationId}/attachments/presign`, {
    method: 'POST',
    body,
  });
}

export function completeChatAttachment(conversationId, body) {
  return chatApi(`/conversations/${conversationId}/attachments/complete`, {
    method: 'POST',
    body,
  });
}

export function deleteChatAttachment(conversationId, key) {
  return chatApi(`/conversations/${conversationId}/attachments/delete`, {
    method: 'POST',
    body: { key },
  });
}

/** Nota persistente no contato (custom_attributes.nota). */
export function updateContactNote(contactId, note) {
  return chatApi(`/contacts/${contactId}/note`, {
    method: 'POST',
    body: { note },
  });
}

/** Escrever com IA (MiMo Token Plan) — refina/gera texto para o composer. */
export function aiWriteComposer({ prompt, draft, history }) {
  return chatApi('/ai-write', {
    method: 'POST',
    body: { prompt, draft, history },
  });
}

/** Upload direto ao S3 (presigned PUT). */
export function putFileToS3(uploadUrl, file, contentType, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', contentType || file.type || 'application/octet-stream');
    xhr.upload.onprogress = (e) => {
      if (!e.lengthComputable || !onProgress) return;
      onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Falha no upload S3 (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error('Falha de rede no upload S3'));
    xhr.send(file);
  });
}

export function fetchEmailReminderCount(conversationId) {
  return chatApi(`/conversations/${conversationId}/email-reminders/count`);
}

export function fetchEmailReminderMailOptions(conversationId) {
  return chatApi(`/conversations/${conversationId}/email-reminders/mail-options`);
}

export function fetchEmailReminder(conversationId, reminderId) {
  return chatApi(`/conversations/${conversationId}/email-reminders/${reminderId}`);
}

export function fetchEmailSignature() {
  return chatApi('/email-signature');
}

export function saveEmailSignature(body) {
  return chatApi('/email-signature', { method: 'PUT', body });
}

export function presignEmailSignature(body) {
  return chatApi('/email-signature/presign', { method: 'POST', body });
}

export async function sendEmailReminder(
  conversationId,
  { to, cc, bcc, subject, bodyHtml, files, empresaCodigo, mailSource }
) {
  const token = localStorage.getItem('authToken');
  const fd = new FormData();
  fd.append('to', JSON.stringify(to || []));
  fd.append('cc', JSON.stringify(cc || []));
  fd.append('bcc', JSON.stringify(bcc || []));
  fd.append('subject', subject || '');
  fd.append('bodyHtml', bodyHtml || '');
  if (empresaCodigo) fd.append('empresaCodigo', String(empresaCodigo));
  if (mailSource) fd.append('mailSource', String(mailSource));
  (files || []).forEach((f) => fd.append('attachments', f));
  const res = await fetch(`${API_BASE}/conversations/${conversationId}/email-reminders`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) {
    const parts = [json.message || `Erro ${res.status}`];
    const detail =
      (typeof json.data === 'string' && json.data) ||
      (typeof json.payload === 'string' && json.payload) ||
      (json.data && typeof json.data.message === 'string' && json.data.message) ||
      '';
    if (detail && detail !== parts[0]) parts.push(detail);
    const err = new Error(parts.filter(Boolean).join(' — '));
    err.status = res.status;
    err.payload = json.data || json.payload || null;
    throw err;
  }
  return json;
}
